import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { useAuth } from '../../context/AuthContext';
import api, { API_BASE } from '../../utils/api';
import { toast } from 'react-toastify';

interface Subscription {
  _id: string;
  name: string;
  price: string;
  duration: string;
  users: string;
  leadAccess: string;
  dailyUnlock: string;
  transactionId: string;
  status: 'pending' | 'approved' | 'rejected' | 'expired';
  createdAt: string;
  startDate?: string;
  expiryDate?: string;
  userId: {
    _id: string;
    name: string;
    email: string;
    username: string;
    role?: string;
  };
}

interface UserRecord {
  _id: string;
  name: string;
  email: string;
  role: string;
  assignedPackage?: { _id: string; name: string; type: string };
  packageExpiry?: string;
}

export default function AdminPackageSubscriptions() {
  const { token } = useAuth();
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'rejected' | 'expired'>('all');
  const [search, setSearch] = useState('');
  const [itemToDelete, setItemToDelete] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);

  // Direct Assign State
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [allUsers, setAllUsers] = useState<UserRecord[]>([]);
  const [allPackages, setAllPackages] = useState<any[]>([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedPkgId, setSelectedPkgId] = useState('');
  const [assignStartDate, setAssignStartDate] = useState('');
  const [assignEndDate, setAssignEndDate] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [userSearch, setUserSearch] = useState('');

  const fetchSubscriptions = async () => {
    try {
      if (!token) return;
      setLoading(true);
      const data = await api.adminGetSubscriptions(token);
      setSubscriptions(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error('Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsersAndPackages = async () => {
    try {
      const [usersData, pkgData] = await Promise.all([
        api.adminGetUsers(token!),
        api.getPackages()
      ]);
      if (Array.isArray(usersData)) setAllUsers(usersData.filter((u: UserRecord) => ['developer', 'broker', 'owner'].includes(u.role)));
      if (Array.isArray(pkgData)) setAllPackages(pkgData);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchSubscriptions();
    if (token) fetchUsersAndPackages();
  }, [token]);

  const handleUpdateStatus = async (id: string, status: 'approved' | 'rejected') => {
    if (!token) return;
    try {
      await api.adminUpdateSubscriptionStatus(token, id, status);
      if (status === 'approved') {
        toast.success('✅ Package approved & automatically assigned to user!');
      } else {
        toast.success('Subscription rejected');
      }
      fetchSubscriptions();
    } catch (err) {
      toast.error(`Failed to update status`);
    }
  };

  const confirmDelete = async () => {
    if (!itemToDelete || !token) return;
    try {
      await api.adminDeleteSubscription(token, itemToDelete);
      toast.success('Subscription deleted successfully');
      fetchSubscriptions();
    } catch (err) {
      toast.error('Failed to delete subscription');
    } finally {
      setItemToDelete(null);
    }
  };

  const confirmBulkDelete = async () => {
    if (selectedIds.length === 0 || !token) return;
    try {
      await api.adminBulkDeleteSubscriptions(token, selectedIds);
      toast.success(`${selectedIds.length} subscriptions deleted successfully`);
      setSelectedIds([]);
      fetchSubscriptions();
    } catch (err) {
      toast.error('Failed to bulk delete subscriptions');
    } finally {
      setShowBulkDeleteModal(false);
    }
  };

  const handleDirectAssign = async () => {
    if (!selectedUserId || !selectedPkgId || !token) return;
    setAssigning(true);
    try {
      const res = await api.adminAssignPackage(token, selectedUserId, selectedPkgId, assignStartDate, assignEndDate);
      if (res.user) {
        toast.success(`✅ Package "${res.user.assignedPackage?.name}" assigned to ${res.user.name}!`);
        setShowAssignModal(false);
        setSelectedUserId('');
        setSelectedPkgId('');
        setAssignStartDate('');
        setAssignEndDate('');
        setUserSearch('');
        fetchUsersAndPackages();
      } else {
        toast.error(res.message || 'Assignment failed');
      }
    } catch (err) {
      toast.error('Server error');
    } finally {
      setAssigning(false);
    }
  };

  const calculateRemainingDays = (expiryDate?: string) => {
    if (!expiryDate) return null;
    const now = new Date();
    const expiry = new Date(expiryDate);
    const diff = expiry.getTime() - now.getTime();
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
    return days > 0 ? days : 0;
  };

  const filteredSubscriptions = subscriptions.filter(sub => {
    const matchesFilter = filter === 'all' || sub.status === filter;
    const searchLower = search.toLowerCase();
    const matchesSearch =
      sub.userId?.name?.toLowerCase().includes(searchLower) ||
      sub.userId?.email?.toLowerCase().includes(searchLower) ||
      sub.name?.toLowerCase().includes(searchLower) ||
      sub.transactionId?.toLowerCase().includes(searchLower);
    return matchesFilter && matchesSearch;
  });

  const filteredUsers = allUsers.filter(u =>
    u.name.toLowerCase().includes(userSearch.toLowerCase()) ||
    u.email.toLowerCase().includes(userSearch.toLowerCase())
  );

  const selectedUser = allUsers.find(u => u._id === selectedUserId);
  const filteredPackagesForUser = selectedUser
    ? allPackages.filter(p => p.type === selectedUser.role || (!p.type))
    : allPackages;

  const getCounts = () => ({
    all: subscriptions.length,
    pending: subscriptions.filter(s => s.status === 'pending').length,
    approved: subscriptions.filter(s => s.status === 'approved').length,
    rejected: subscriptions.filter(s => s.status === 'rejected').length,
    expired: subscriptions.filter(s => s.status === 'expired').length,
  });

  const counts = getCounts();

  return (
    <AdminLayout>
      <div className="-mx-4 md:-mx-6 lg:-ml-6 lg:-mr-6 py-3 min-h-screen font-sans bg-slate-50">
        <div className="w-full mx-auto space-y-3 px-0">

          {/* Top Banner/Header */}
          <div className="bg-white rounded-2xl p-4 md:p-6 border border-slate-200 shadow-sm flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-slate-900 leading-none">Purchased Packages</h1>
              <p className="text-sm text-slate-500 font-medium mt-2">Manage and assign property listing packages purchased by users via UPI.</p>
            </div>
            <div className="flex gap-2">
              {selectedIds.length > 0 && (
                <button
                  onClick={() => setShowBulkDeleteModal(true)}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-rose-600 text-white font-bold text-sm hover:bg-rose-700 transition-all shadow-md active:scale-95"
                  title="Delete Selected"
                >
                  <i className="fa-solid fa-trash"></i>
                  Delete ({selectedIds.length})
                </button>
              )}
              {/* ✅ DIRECT ASSIGN BUTTON */}
              <button
                onClick={() => setShowAssignModal(true)}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#0F2A5F] text-white font-bold text-sm hover:bg-black transition-all shadow-md active:scale-95"
                title="Manually assign a package to any user"
              >
                <i className="fa-solid fa-crown text-amber-400"></i>
                Direct Assign
              </button>
              <button
                onClick={fetchSubscriptions}
                className="w-10 h-10 rounded-md bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-all flex items-center justify-center shadow-sm active:scale-95"
                title="Refresh"
              >
                <i className="fa-solid fa-arrows-rotate"></i>
              </button>
            </div>
          </div>

          {/* Counts & Filter Tabs */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
            {[
              { type: 'all', label: 'All', count: counts.all, color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-100', icon: 'fa-layer-group' },
              { type: 'pending', label: 'Pending', count: counts.pending, color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-100', icon: 'fa-clock' },
              { type: 'approved', label: 'Assigned', count: counts.approved, color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-100', icon: 'fa-check-circle' },
              { type: 'rejected', label: 'Rejected', count: counts.rejected, color: 'text-rose-600', bg: 'bg-rose-50', border: 'border-rose-100', icon: 'fa-times-circle' },
              { type: 'expired', label: 'Expired', count: counts.expired, color: 'text-slate-600', bg: 'bg-slate-100', border: 'border-slate-200', icon: 'fa-ban' },
            ].map(tab => (
              <button
                key={tab.type}
                onClick={() => setFilter(tab.type as any)}
                className={`p-4 rounded-xl border transition-all duration-200 text-left flex items-center justify-between group ${
                  filter === tab.type
                    ? 'bg-white border-blue-500 ring-1 ring-blue-500 shadow-sm'
                    : 'bg-white border-slate-200 hover:border-slate-300 shadow-sm'
                }`}
              >
                <div>
                  <span className={`text-xs font-bold tracking-wide uppercase ${tab.color}`}>{tab.label}</span>
                  <h3 className="text-xl font-bold text-slate-900 mt-1">{tab.count}</h3>
                </div>
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-lg ${tab.bg} ${tab.color} border ${tab.border}`}>
                  <i className={`fa-solid ${tab.icon}`}></i>
                </div>
              </button>
            ))}
          </div>

          {/* Search & Main Table Area */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-5 border-b border-slate-200 bg-white flex flex-col md:flex-row gap-4 justify-between items-center">
              <div className="relative flex-1 max-w-md w-full">
                <i className="fa-solid fa-search absolute top-1/2 left-3.5 -translate-y-1/2 text-slate-400 text-sm"></i>
                <input
                  type="text"
                  placeholder="Search user, email, plan or full UTR..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-slate-300 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-sm text-slate-900 transition-all"
                />
              </div>
              <div className="text-sm font-medium text-slate-500">
                Matches: <span className="text-slate-900 font-bold">{filteredSubscriptions.length}</span>
              </div>
            </div>

            <div className="overflow-x-auto">
              {loading ? (
                <div className="p-16 flex flex-col items-center justify-center text-center">
                  <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
                  <p className="text-sm font-medium text-slate-500">Loading purchases...</p>
                </div>
              ) : filteredSubscriptions.length === 0 ? (
                <div className="p-16 text-center flex flex-col items-center justify-center">
                  <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center text-slate-400 text-2xl mb-4">
                    <i className="fa-solid fa-inbox"></i>
                  </div>
                  <h3 className="text-lg font-bold text-slate-900">No Purchases Found</h3>
                  <p className="text-sm text-slate-500 mt-1 max-w-sm">
                    No records match your current filters and search query.
                  </p>
                </div>
              ) : (
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="px-6 py-3.5 text-xs font-bold text-slate-500 uppercase tracking-wide w-12 text-center">
                        <input
                          type="checkbox"
                          className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                          checked={filteredSubscriptions.length > 0 && selectedIds.length === filteredSubscriptions.length}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedIds(filteredSubscriptions.map(s => s._id));
                            } else {
                              setSelectedIds([]);
                            }
                          }}
                        />
                      </th>
                      <th className="px-6 py-3.5 text-xs font-bold text-slate-500 uppercase tracking-wide">User</th>
                      <th className="px-6 py-3.5 text-xs font-bold text-slate-500 uppercase tracking-wide">Plan & UTR</th>
                      <th className="px-6 py-3.5 text-xs font-bold text-slate-500 uppercase tracking-wide">Dates</th>
                      <th className="px-6 py-3.5 text-xs font-bold text-slate-500 uppercase tracking-wide text-center">Days Left</th>
                      <th className="px-6 py-3.5 text-xs font-bold text-slate-500 uppercase tracking-wide">Status</th>
                      <th className="px-6 py-3.5 text-xs font-bold text-slate-500 uppercase tracking-wide text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {filteredSubscriptions.map(sub => (
                      <tr key={sub._id} className={`hover:bg-slate-50/50 transition-colors ${selectedIds.includes(sub._id) ? 'bg-blue-50/30' : ''}`}>
                        <td className="px-6 py-4 text-center">
                          <input
                            type="checkbox"
                            className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                            checked={selectedIds.includes(sub._id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedIds(prev => [...prev, sub._id]);
                              } else {
                                setSelectedIds(prev => prev.filter(id => id !== sub._id));
                              }
                            }}
                          />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-sm shrink-0">
                              {(sub.userId?.name || 'U').charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <p className="text-sm font-bold text-slate-900">{sub.userId?.name || 'Unknown User'}</p>
                              <p className="text-xs text-slate-500 mt-0.5">{sub.userId?.email || 'N/A'}</p>
                              {sub.userId?.role && (
                                <span className="text-[10px] font-bold uppercase tracking-wider text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded mt-0.5 inline-block">
                                  {sub.userId.role}
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <p className="text-sm font-bold text-slate-900">{sub.name}</p>
                            <div className="flex gap-1 items-baseline mt-1 mb-2">
                              <span className="text-xs font-bold text-slate-700">₹{sub.price.replace(/[^0-9]/g, '')}</span>
                              <span className="text-xs text-slate-500">/ {sub.duration}</span>
                            </div>
                            <div className="inline-flex flex-col gap-1">
                              <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Transaction UTR</span>
                              <span className="text-sm font-mono text-blue-700 bg-blue-50 px-2 py-1 rounded border border-blue-100 shadow-sm">{sub.transactionId.toUpperCase()}</span>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-col gap-0.5 text-xs">
                            <div className="flex gap-1">
                              <span className="font-semibold text-slate-500 w-12">Start:</span>
                              <span className="text-slate-900">{sub.startDate ? new Date(sub.startDate).toLocaleDateString() : 'N/A'}</span>
                            </div>
                            <div className="flex gap-1">
                              <span className="font-semibold text-slate-500 w-12">End:</span>
                              <span className={`font-medium ${!sub.expiryDate ? 'text-emerald-600' : 'text-slate-900'}`}>
                                {sub.expiryDate ? new Date(sub.expiryDate).toLocaleDateString() : '∞ Lifetime'}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-2 text-center">
                          {sub.status === 'approved' ? (
                            !sub.expiryDate ? (
                              // No expiry = Unlimited / Free Forever
                              <span className="inline-flex items-center justify-center gap-1 px-2.5 py-1 rounded-md text-xs font-bold bg-emerald-100 text-emerald-700">
                                <i className="fa-solid fa-infinity text-[10px]"></i> Lifetime
                              </span>
                            ) : (
                              <span className={`inline-flex items-center justify-center px-2.5 py-1 rounded-md text-xs font-bold ${
                                (calculateRemainingDays(sub.expiryDate) || 0) <= 7
                                  ? 'bg-rose-100 text-rose-700'
                                  : 'bg-slate-100 text-slate-700'
                              }`}>
                                {calculateRemainingDays(sub.expiryDate)} days
                              </span>
                            )
                          ) : sub.status === 'expired' ? (
                            <span className="inline-flex items-center justify-center px-2.5 py-1 rounded-md text-xs font-bold bg-slate-100 text-slate-500">0 days</span>
                          ) : (
                            <span className="text-xs text-slate-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-2">
                          {sub.status === 'approved' ? (
                            <span className="px-2.5 py-1 bg-emerald-100 text-emerald-700 rounded-md font-bold text-xs inline-flex items-center gap-1.5">
                              <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></span> Assigned
                            </span>
                          ) : sub.status === 'rejected' ? (
                            <span className="px-2.5 py-1 bg-rose-100 text-rose-700 rounded-md font-bold text-xs inline-flex items-center gap-1.5">
                              <span className="w-1.5 h-1.5 bg-rose-500 rounded-full"></span> Rejected
                            </span>
                          ) : sub.status === 'expired' ? (
                            <span className="px-2.5 py-1 bg-slate-100 text-slate-600 rounded-md font-bold text-xs inline-flex items-center gap-1.5">
                              <span className="w-1.5 h-1.5 bg-slate-400 rounded-full"></span> Expired
                            </span>
                          ) : (
                            <span className="px-2.5 py-1 bg-amber-100 text-amber-700 rounded-md font-bold text-xs inline-flex items-center gap-1.5">
                              <span className="w-1.5 h-1.5 bg-amber-500 rounded-full"></span> Pending Verification
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-right">
                          <div className="flex justify-end gap-2">
                            {(sub.status === 'pending' || sub.status === 'expired' || sub.status === 'rejected') && (
                              <button
                                onClick={() => handleUpdateStatus(sub._id, 'approved')}
                                className="px-3 py-1.5 rounded-md bg-[#0F2A5F] border border-[#05144b] hover:bg-black text-white font-bold text-xs transition-all shadow-md active:scale-95 flex items-center gap-1.5"
                              >
                                <i className="fa-solid fa-crown text-amber-400 text-[10px]"></i>
                                Assign Plan
                              </button>
                            )}
                            {sub.status !== 'rejected' && sub.status !== 'expired' && (
                              <button
                                onClick={() => handleUpdateStatus(sub._id, 'rejected')}
                                className="px-3 py-1.5 rounded-md bg-white border border-rose-200 hover:bg-rose-50 text-rose-600 font-bold text-xs transition-all shadow-sm"
                              >
                                Reject
                              </button>
                            )}
                            <button
                              onClick={() => setItemToDelete(sub._id)}
                              className="w-8 h-8 rounded-md bg-white border border-rose-200 hover:bg-rose-50 text-rose-600 font-bold text-xs transition-all shadow-sm flex items-center justify-center"
                              title="Delete Subscription"
                            >
                              <i className="fa-solid fa-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ✅ DIRECT ASSIGN MODAL */}
      {showAssignModal && (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center px-4">
          <div
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            onClick={() => setShowAssignModal(false)}
          />
          <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden z-10">
            {/* Modal Header */}
            <div className="bg-[#0F2A5F] p-6 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-400/20 rounded-xl flex items-center justify-center">
                  <i className="fa-solid fa-crown text-amber-400 text-lg"></i>
                </div>
                <div>
                  <h2 className="text-lg font-black text-white">Direct Package Assign</h2>
                  <p className="text-xs text-blue-300 font-medium">Manually assign a package to any user</p>
                </div>
              </div>
              <button
                onClick={() => setShowAssignModal(false)}
                className="w-9 h-9 rounded-lg bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-all"
              >
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-5">
              {/* User Search + Select */}
              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">Select User</label>
                <div className="relative">
                  <i className="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs"></i>
                  <input
                    type="text"
                    placeholder="Search developer / broker..."
                    value={userSearch}
                    onChange={e => setUserSearch(e.target.value)}
                    className="w-full pl-9 pr-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-[#0F2A5F] focus:ring-1 focus:ring-[#0F2A5F]"
                  />
                </div>
                <select
                  value={selectedUserId}
                  onChange={e => { setSelectedUserId(e.target.value); setSelectedPkgId(''); }}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 focus:outline-none focus:border-[#0F2A5F] focus:ring-1 focus:ring-[#0F2A5F] bg-white"
                  size={4}
                >
                  <option value="">-- Choose User --</option>
                  {filteredUsers.map(u => (
                    <option key={u._id} value={u._id}>
                      {u.name} ({u.email}) [{u.role}]{u.assignedPackage ? ` ✓ ${u.assignedPackage.name}` : ''}
                    </option>
                  ))}
                </select>
                {selectedUser && (
                  <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <div className="w-8 h-8 rounded-full bg-[#0F2A5F] flex items-center justify-center text-white font-bold text-sm">
                      {selectedUser.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="text-sm font-bold text-slate-900">{selectedUser.name}</p>
                      <p className="text-xs text-slate-500">{selectedUser.email} · {selectedUser.role}</p>
                      {selectedUser.assignedPackage && (
                        <p className="text-xs text-amber-600 font-bold">Current: {selectedUser.assignedPackage.name}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Package Select */}
              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">Select Package</label>
                <select
                  value={selectedPkgId}
                  onChange={e => setSelectedPkgId(e.target.value)}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 focus:outline-none focus:border-[#0F2A5F] focus:ring-1 focus:ring-[#0F2A5F] bg-white"
                  disabled={!selectedUserId}
                >
                  <option value="">-- Choose Package --</option>
                  {filteredPackagesForUser.map(pkg => (
                    <option key={pkg._id} value={pkg._id}>
                      {pkg.name} — ₹{pkg.price} / {pkg.period} [{pkg.type}]
                    </option>
                  ))}
                </select>
              </div>

              {/* Optional Date Range */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">Start Date <span className="text-slate-400 normal-case font-normal">(optional)</span></label>
                  <input
                    type="date"
                    value={assignStartDate}
                    onChange={e => setAssignStartDate(e.target.value)}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-[#0F2A5F] focus:ring-1 focus:ring-[#0F2A5F]"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">Expiry Date <span className="text-slate-400 normal-case font-normal">(optional)</span></label>
                  <input
                    type="date"
                    value={assignEndDate}
                    onChange={e => setAssignEndDate(e.target.value)}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-[#0F2A5F] focus:ring-1 focus:ring-[#0F2A5F]"
                  />
                </div>
              </div>

              <p className="text-xs text-slate-400 font-medium">
                <i className="fa-solid fa-info-circle mr-1"></i>
                Leave dates empty to use auto-calculated expiry based on package period. For unlimited packages, no expiry is set.
              </p>
            </div>

            {/* Modal Footer */}
            <div className="px-6 pb-6 flex gap-3">
              <button
                onClick={() => setShowAssignModal(false)}
                className="flex-1 py-3 rounded-xl border border-slate-200 text-slate-600 font-bold text-sm hover:bg-slate-50 transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleDirectAssign}
                disabled={assigning || !selectedUserId || !selectedPkgId}
                className="flex-[2] py-3 rounded-xl bg-[#0F2A5F] text-white font-black text-sm hover:bg-black transition-all shadow-lg disabled:opacity-40 disabled:cursor-not-allowed active:scale-95 flex items-center justify-center gap-2"
              >
                {assigning ? (
                  <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div> Assigning...</>
                ) : (
                  <><i className="fa-solid fa-crown text-amber-400"></i> Confirm Assignment</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Delete Confirmation Modal */}
      {itemToDelete && (
        <div className="fixed inset-0 z-[3000] flex items-center justify-center px-4">
          <div
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            onClick={() => setItemToDelete(null)}
          />
          <div className="relative w-full max-w-sm bg-white rounded-2xl shadow-2xl overflow-hidden z-10 animate__animated animate__zoomIn animate__faster">
            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4 text-rose-600 text-2xl shadow-sm">
                <i className="fa-solid fa-triangle-exclamation"></i>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Delete Record?</h3>
              <p className="text-sm text-slate-500 font-medium">This action cannot be undone. Are you sure you want to permanently delete this subscription record?</p>
            </div>
            <div className="p-4 bg-slate-50 border-t border-slate-100 flex gap-3">
              <button
                onClick={() => setItemToDelete(null)}
                className="flex-1 py-2.5 rounded-xl bg-white border border-slate-300 text-slate-700 font-bold text-sm hover:bg-slate-50 transition-all shadow-sm active:scale-95"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="flex-1 py-2.5 rounded-xl bg-rose-600 text-white font-bold text-sm hover:bg-rose-700 transition-all shadow-sm active:scale-95"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteModal && (
        <div className="fixed inset-0 z-[3000] flex items-center justify-center px-4">
          <div
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            onClick={() => setShowBulkDeleteModal(false)}
          />
          <div className="relative w-full max-w-sm bg-white rounded-2xl shadow-2xl overflow-hidden z-10 animate__animated animate__zoomIn animate__faster">
            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4 text-rose-600 text-2xl shadow-sm">
                <i className="fa-solid fa-trash-can"></i>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Delete {selectedIds.length} Records?</h3>
              <p className="text-sm text-slate-500 font-medium">This action cannot be undone. Are you sure you want to permanently delete {selectedIds.length} subscription records?</p>
            </div>
            <div className="p-4 bg-slate-50 border-t border-slate-100 flex gap-3">
              <button
                onClick={() => setShowBulkDeleteModal(false)}
                className="flex-1 py-2.5 rounded-xl bg-white border border-slate-300 text-slate-700 font-bold text-sm hover:bg-slate-50 transition-all shadow-sm active:scale-95"
              >
                Cancel
              </button>
              <button
                onClick={confirmBulkDelete}
                className="flex-1 py-2.5 rounded-xl bg-rose-600 text-white font-bold text-sm hover:bg-rose-700 transition-all shadow-sm active:scale-95"
              >
                Delete Selected
              </button>
            </div>
          </div>
        </div>
      )}

    </AdminLayout>
  );
}
